import { FC } from 'react';
import {
  RouteComponentProps,
  useHistory,
  useLocation,
  withRouter
} from 'react-router-dom';
import cc from 'classcat';
import qs from 'qs';
import { EIconSize } from 'utils/constants/app/ui';
import {
  getMountPath,
  mountPoint,
  sbPathFromObj
} from 'utils/generic/route-manipulation';
import { NavSectionItem, PageType } from 'interfaces/generic';
import { ERacingPageTypes } from 'interfaces/racing';
import { GlobalIcon } from 'components/GlobalIcon';
import { StandaloneGamesCount } from 'components/StandaloneGamesCount';
import { WhiteTooltip } from 'components/WhiteTooltip';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSearchParams } from 'hooks/useSearchParams';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import './index.less';

type Props = RouteComponentProps & {
  title: string;
  items: NavSectionItem[];
  routes: Record<PageType, string>;
  pageType: PageType;
  category?: string;
  externalOnClick?: () => void;
  tooltipOnParent?: boolean;
};

const _NavSection: FC<Props> = ({
  title,
  items,
  pageType: type,
  routes,
  category,
  externalOnClick,
  tooltipOnParent
}) => {
  const history = useHistory();
  const location = useLocation();
  const queries = useSearchParams();
  const { path, params } = useSbRouteMatch();
  const configs = useSportsbookConfigs();

  return (
    <div className="navSection__container">
      <div className="navSection__title">
        <span>{title}</span>
      </div>
      <div>
        {items.map(
          ({
            id,
            title,
            pageType,
            countGetter,
            queryStringObject,
            iconInfo: { lib, name, theme }
          }) => (
            <div
              className={cc([
                'navSection__sectionItem',
                {
                  'navSection__sectionItem--active':
                    pageType === 'racing'
                      ? +params.sport === +id
                      : pageType === (queries.type || type) ||
                        (queries.type === 'racing' &&
                          ((queryStringObject &&
                            queries.type === queryStringObject.type) ||
                            (!queries.type && id === 1)))
                }
              ])}
              key={id}
              onClick={() => {
                if (queryStringObject?.type === queries.type) {
                  return;
                }

                externalOnClick?.();

                if (pageType === 'racing') {
                  history.push(
                    `${mountPoint(location.pathname)}/${
                      ERacingPageTypes.UPCOMING
                    }/${id}`
                  );

                  return;
                }

                if (queryStringObject) {
                  const sbObj: Partial<{
                    sport: string;
                    competition: string;
                    region: string;
                    game: string;
                  }> = {};

                  if (configs.category === 'esport') {
                    sbObj.sport = 'AllESports';
                    sbObj.region = 'all';
                    sbObj.competition = 'all';
                  } else {
                    if (params.sport) {
                      sbObj.sport = params.sport;
                    }

                    if (params.competition) {
                      sbObj.competition = params.competition;
                    }

                    if (params.region) {
                      sbObj.region = params.region;
                    }
                  }

                  history.push({
                    pathname: `${getMountPath(path)}${sbPathFromObj(sbObj)}`,
                    search: qs.stringify(queryStringObject)
                  });

                  return;
                }

                pageType !== queries.type &&
                  history.push(routes[pageType as PageType]);
              }}
            >
              <div className="navSection__sectionItemDetails">
                <GlobalIcon
                  lib={lib}
                  name={name}
                  theme={theme}
                  size={EIconSize._32}
                />
                <WhiteTooltip title={title} tooltipOnParent={tooltipOnParent}>
                  <div className="navSection__selectionItemName">{title}</div>
                </WhiteTooltip>
              </div>
              {countGetter && (
                <div className="navSection__gamesCount">
                  <StandaloneGamesCount
                    countGetter={countGetter}
                    currentSport={queries.sport as string}
                    category={category}
                    pageType={(queries.type || type) as PageType}
                  />
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export const NavSection: ReturnType<typeof withRouter> | any = withRouter(
  _NavSection as any
);
